export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
    throw new ApiError(401, "AUTH_REQUIRED", "Not authenticated");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText, code: "UNKNOWN" }));
    throw new ApiError(response.status, body.code ?? "UNKNOWN", body.error ?? "Request failed", body.details);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface StreamEvent {
  type: string;
  [key: string]: unknown;
}

export const api = {
  async streamPost(
    url: string,
    body: unknown,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });

    if (res.status === 401) {
      window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
      throw new ApiError(401, "AUTH_REQUIRED", "Not authenticated");
    }

    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({ error: res.statusText, code: "UNKNOWN" }));
      throw new ApiError(res.status, errorBody.code ?? "UNKNOWN", errorBody.error ?? "Request failed");
    }

    if (!res.body) throw new ApiError(500, "NO_STREAM", "No response body");

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("data: ")) {
          const json = trimmed.slice(6);
          if (json === "[DONE]") return;
          try {
            onEvent(JSON.parse(json) as StreamEvent);
          } catch {
            // Skip malformed lines
          }
        }
      }
    }
  },

  async get<T>(url: string): Promise<T> {
    const res = await fetch(url, { credentials: "include" });
    return handleResponse<T>(res);
  },

  async post<T>(url: string, body?: unknown): Promise<T> {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(res);
  },

  async put<T>(url: string, body: unknown): Promise<T> {
    const res = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return handleResponse<T>(res);
  },

  async delete<T>(url: string): Promise<T> {
    const res = await fetch(url, { method: "DELETE", credentials: "include" });
    return handleResponse<T>(res);
  },
};
